import { useCallback, useEffect, useState } from "react";
import { api, formatErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, CalendarClock } from "lucide-react";
import { PreventiveCompleteDialog } from "@/components/PreventiveCompleteDialog";

const TechCell = ({ s }) => (
  <TableCell className="text-sm">
    {s.technician_names?.length ? s.technician_names.join(", ") : <span className="text-muted-foreground italic">Não atribuído</span>}
    {s.last_completed_by_names?.length > 0 && (
      <div className="text-[11px] text-green-700 dark:text-green-400" data-testid={`last-exec-${s.id}`}>Últ. execução: {s.last_completed_by_names.join(", ")}</div>
    )}
  </TableCell>
);

const TASKS = ["Lubrificação", "Inspeção Visual", "Verificação Elétrica", "Alinhamento", "Troca de Óleo", "Limpeza", "Aperto de Parafusos", "Outro"];

const EMPTY = { equipment_tag: "", task: "Lubrificação", interval_days: 30, last_service_date: "", technician_ids: [] };

export default function PreventiveMaintenance() {
  const { user } = useAuth();
  const canEdit = ["supervisor", "almoxarife", "tecnico"].includes(user?.role);
  const isSupervisor = ["supervisor", "almoxarife"].includes(user?.role);
  const [schedules, setSchedules] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(() => api.get("/preventive").then((r) => setSchedules(r.data)), []);

  useEffect(() => {
    load();
    api.get("/equipments").then((r) => setEquipments(r.data));
    api.get("/users/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, [load]);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };

  const save = async () => {
    if (!form.equipment_tag) return toast.error("Selecione o equipamento");
    if (!form.task.trim()) return toast.error("Descreva a tarefa");
    setSaving(true);
    try {
      await api.post("/preventive", {
        equipment_tag: form.equipment_tag,
        task: form.task,
        interval_days: Number(form.interval_days),
        last_service_date: form.last_service_date || undefined,
        technician_ids: form.technician_ids || [],
      });
      toast.success("Manutenção preventiva agendada");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const complete = (id) => setCompleting(schedules.find((s) => s.id === id));

  const remove = async (id) => {
    try {
      await api.delete(`/preventive/${id}`);
      toast.success("Agendamento excluído");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const overdue = schedules.filter((s) => s.days_overdue > 0);
  const upcoming = schedules.filter((s) => s.days_overdue === 0);

  const statusBadge = (s) => {
    if (s.days_overdue > 0) return <Badge className="rounded-sm bg-red-500/10 text-red-700 border-red-500/30 border" variant="outline">Vencida há {s.days_overdue}d</Badge>;
    if (s.days_until_due <= 7) return <Badge className="rounded-sm bg-yellow-500/10 text-yellow-700 border-yellow-500/30 border" variant="outline">Em {s.days_until_due}d</Badge>;
    return <Badge className="rounded-sm bg-green-500/10 text-green-700 border-green-500/30 border" variant="outline">Em dia</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="preventive-page">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Planejamento</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Manutenção Preventiva</h1>
          <p className="text-muted-foreground mt-1">Inspeções e lubrificações agendadas por equipamento.</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="new-preventive-btn">
            <Plus className="w-4 h-4" /> Nova Preventiva
          </Button>
        )}
      </div>

      {/* Overdue */}
      {overdue.length > 0 && (
        <Card className="rounded-sm shadow-none border-red-500/40 border-2">
          <div className="p-4 border-b flex items-center gap-2 bg-red-500/5">
            <CalendarClock className="w-4 h-4 text-red-600" />
            <div className="font-heading font-bold text-red-600">Vencidas ({overdue.length})</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>TAG</TableHead>
                <TableHead>Equipamento</TableHead>
                <TableHead>Tarefa</TableHead>
                <TableHead>Técnicos</TableHead>
                <TableHead>Vencia em</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {overdue.map((s) => (
                <TableRow key={s.id} data-testid={`preventive-row-${s.id}`}>
                  <TableCell className="font-mono-tag font-semibold text-orange-600">{s.equipment_tag}</TableCell>
                  <TableCell>{s.equipment_name}</TableCell>
                  <TableCell>{s.task}</TableCell>
                  <TechCell s={s} />
                  <TableCell className="text-sm">{s.next_due_date}</TableCell>
                  <TableCell>{statusBadge(s)}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => complete(s.id)} className="rounded-sm gap-1.5 h-8" data-testid={`complete-${s.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Concluir
                      </Button>
                      {isSupervisor && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1.5 text-red-600 hover:text-red-700" data-testid={`delete-preventive-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Excluir agendamento?</AlertDialogTitle>
                              <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(s.id)} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Upcoming */}
      <Card className="rounded-sm shadow-none">
        <div className="p-4 border-b flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-orange-500" />
          <div className="font-heading font-bold">Próximos Serviços ({upcoming.length})</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>TAG</TableHead>
              <TableHead>Equipamento</TableHead>
              <TableHead>Tarefa</TableHead>
              <TableHead>Técnicos</TableHead>
              <TableHead>Última execução</TableHead>
              <TableHead>Próximo vencimento</TableHead>
              <TableHead>Intervalo</TableHead>
              <TableHead>Status</TableHead>
              {canEdit && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {upcoming.length === 0 ? (
              <TableRow><TableCell colSpan={canEdit ? 9 : 8} className="text-center text-muted-foreground py-8">Nenhum agendamento cadastrado</TableCell></TableRow>
            ) : upcoming.map((s) => (
              <TableRow key={s.id} data-testid={`preventive-row-${s.id}`}>
                <TableCell className="font-mono-tag font-semibold text-orange-600">{s.equipment_tag}</TableCell>
                <TableCell>{s.equipment_name}</TableCell>
                <TableCell>{s.task}</TableCell>
                <TechCell s={s} />
                <TableCell className="text-sm">{s.last_service_date}</TableCell>
                <TableCell className="text-sm">{s.next_due_date}</TableCell>
                <TableCell className="text-sm">{s.interval_days}d</TableCell>
                <TableCell>{statusBadge(s)}</TableCell>
                {canEdit && (
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => complete(s.id)} className="rounded-sm gap-1.5 h-8" data-testid={`complete-${s.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Concluir
                    </Button>
                    {isSupervisor && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1.5 text-red-600 hover:text-red-700" data-testid={`delete-preventive-${s.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir agendamento?</AlertDialogTitle>
                            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(s.id)} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="preventive-dialog">
          <DialogHeader>
            <DialogTitle>Nova Manutenção Preventiva</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Equipamento *</Label>
              <Select value={form.equipment_tag} onValueChange={(v) => setForm({ ...form, equipment_tag: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="preventive-equipment"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {equipments.map((e) => (
                    <SelectItem key={e.tag} value={e.tag}>
                      <span className="font-mono-tag mr-2 text-orange-600">{e.tag}</span> {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tarefa *</Label>
              <Select value={form.task} onValueChange={(v) => setForm({ ...form, task: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="preventive-task"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASKS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Intervalo (dias) *</Label>
                <Input type="number" min={1} value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} className="rounded-sm mt-1.5" data-testid="preventive-interval" />
              </div>
              <div>
                <Label>Última execução</Label>
                <Input type="date" value={form.last_service_date} onChange={(e) => setForm({ ...form, last_service_date: e.target.value })} className="rounded-sm mt-1.5" data-testid="preventive-last-date" />
                <div className="text-[10px] text-muted-foreground mt-1">Deixe em branco para usar hoje</div>
              </div>
            </div>
            <div>
              <Label>Técnicos Responsáveis</Label>
              <div className="mt-1.5 space-y-1.5 border rounded-sm p-2 max-h-40 overflow-y-auto bg-background">
                {technicians.length === 0 && <div className="text-xs text-muted-foreground p-1">Nenhum técnico cadastrado</div>}
                {technicians.map((t) => {
                  const checked = (form.technician_ids || []).includes(t.id);
                  return (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary p-1 rounded-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        data-testid={`prev-tech-checkbox-${t.id}`}
                        onChange={(e) => {
                          const next = new Set(form.technician_ids || []);
                          if (e.target.checked) next.add(t.id); else next.delete(t.id);
                          setForm({ ...form, technician_ids: Array.from(next) });
                        }}
                        className="w-4 h-4 rounded-sm accent-orange-500"
                      />
                      <span className="flex-1">{t.name}</span>
                      <span className="text-xs text-muted-foreground">{t.email}</span>
                    </label>
                  );
                })}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Técnicos atribuídos recebem o alerta na aproximação do vencimento. Supervisores só são notificados se atrasar.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="preventive-save">
              {saving ? "Salvando..." : "Agendar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PreventiveCompleteDialog schedule={completing} technicians={technicians} onClose={() => setCompleting(null)} onDone={load} />
    </div>
  );
}
